/**
 * AnchoredPopover — popover ancorado a um trigger, renderizado via portal no
 * document.body.
 *
 * Por que portal: um popover `absolute` herda o stacking context dos seus
 * ancestrais. Quando algum ancestral (ex: a sidebar `fixed z-50`, ou qualquer
 * wrapper com transform/opacity) cria um contexto proprio, o popover fica
 * preso abaixo dele e nenhum z-index local o resolve. Renderizando no body com
 * `position: fixed`, ele escapa de qualquer contexto local e fica acima de
 * tudo (sidebar inclusa). Mesmo mecanismo ja usado no mention-editor.tsx.
 *
 * Posicionamento: calcula a partir do getBoundingClientRect do trigger e faz
 * clamp na viewport pra nunca estourar nem ficar atras da sidebar. Reposiciona
 * em scroll/resize. Fechar fica por conta de:
 *   - clique fora (trigger + conteudo do popover sao considerados "dentro")
 *   - tecla Escape
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

export interface AnchoredPopoverProps {
  /** Ref do elemento-gatilho (o botao que abre o popover). */
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Largura do painel em px. Default 320 (w-80). */
  width?: number;
  /**
   * Alinhamento horizontal em relacao ao trigger:
   * - 'start': borda esquerda do painel alinha a esquerda do trigger
   * - 'end': borda direita do painel alinha a direita do trigger
   * - 'center': painel centralizado sob o trigger
   * Em qualquer caso o resultado e clampado na viewport.
   * Default 'start'.
   */
  align?: 'start' | 'end' | 'center';
  /** Espaco vertical entre trigger e painel, em px. Default 4. */
  offset?: number;
  /** Classe extra do painel. */
  className?: string;
}

// Margem minima das bordas da viewport e da sidebar.
const VIEWPORT_MARGIN = 8;
// Largura da sidebar expandida (w-60 = 15rem = 240px). Em telas md+ a sidebar
// e fixa a esquerda; manter o painel a partir dela evita o corte. Em telas
// menores (sidebar off-canvas) o clamp simples na margem ja basta.
const SIDEBAR_WIDTH_MD = 240;
const MD_BREAKPOINT = 768;

interface Coords {
  top: number;
  left: number;
  maxHeight: number;
}

export function AnchoredPopover({
  anchorRef,
  open,
  onClose,
  children,
  width = 320,
  align = 'start',
  offset = 4,
  className,
}: AnchoredPopoverProps) {
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = React.useState<Coords | null>(null);

  // measuredHeight: altura real do painel, quando ja disponivel (o painel so
  // existe no DOM depois da 1a renderizacao com coords != null — antes disso
  // nao ha o que medir). Usada so pra decidir o flip vertical; sem ela o
  // calculo assume "abre embaixo", que e o comportamento de sempre.
  const computeCoords = React.useCallback((measuredHeight: number | null): Coords | null => {
    const anchor = anchorRef.current;
    if (!anchor) return null;
    const rect = anchor.getBoundingClientRect();

    let left: number;
    switch (align) {
      case 'end':
        left = rect.right - width;
        break;
      case 'center':
        left = rect.left + rect.width / 2 - width / 2;
        break;
      case 'start':
      default:
        left = rect.left;
        break;
    }

    // Limite esquerdo: nao deixar entrar embaixo da sidebar em telas md+.
    const minLeft =
      window.innerWidth >= MD_BREAKPOINT
        ? SIDEBAR_WIDTH_MD + VIEWPORT_MARGIN
        : VIEWPORT_MARGIN;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    // Se a viewport for estreita demais, prioriza colar na margem esquerda.
    left = Math.max(minLeft, Math.min(left, Math.max(minLeft, maxLeft)));

    const spaceBelow = window.innerHeight - rect.bottom - offset - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - offset - VIEWPORT_MARGIN;

    // Flip vertical: so abre pra cima quando NAO cabe embaixo E cabe em cima.
    // Retrocompativel — sem altura medida (1a passada) ou quando cabe embaixo,
    // cai sempre no branch "embaixo" identico ao comportamento anterior.
    if (measuredHeight != null && measuredHeight > spaceBelow && measuredHeight <= spaceAbove) {
      return { top: rect.top - offset - measuredHeight, left, maxHeight: spaceAbove };
    }
    return { top: rect.bottom + offset, left, maxHeight: Math.max(spaceBelow, 0) };
  }, [anchorRef, align, width, offset]);

  // Posiciona ao abrir e reposiciona em scroll/resize enquanto aberto.
  React.useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const update = () => {
      // scrollHeight (nao getBoundingClientRect().height): precisa da altura
      // NATURAL do conteudo, nao da altura ja cortada pelo maxHeight/overflow
      // aplicados no render anterior — senao o flip nunca dispara, porque a
      // altura medida ja reflete o corte em vez do tamanho desejado.
      const measured = panelRef.current?.scrollHeight ?? null;
      setCoords(computeCoords(measured));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, computeCoords]);

  // A 1a passada acima roda antes do painel existir no DOM (coords ainda era
  // null), entao nao ha altura real pra medir. Assim que o painel monta,
  // mede a altura de verdade e reaplica o calculo — e isso que de fato aciona
  // o flip vertical quando necessario. Converge em 1 passada extra: se o
  // resultado nao mudar (caso comum, cabe embaixo), nao ha novo setState.
  React.useLayoutEffect(() => {
    if (!open || !coords || !panelRef.current) return;
    const measured = panelRef.current.scrollHeight;
    const next = computeCoords(measured);
    if (next && (next.top !== coords.top || next.maxHeight !== coords.maxHeight)) {
      setCoords(next);
    }
  }, [open, coords, computeCoords]);

  // Fechar: clique fora (fora do trigger E fora do painel) ou Escape.
  React.useEffect(() => {
    if (!open) return;
    function onDocPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !coords) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={cn(
        'fixed z-[70] overflow-hidden rounded-md border shadow-lg',
        'border-[var(--popover-border,var(--card-border))]',
        'backdrop-blur-[var(--blur,0px)]',
        className,
      )}
      style={{
        top: coords.top,
        left: coords.left,
        width,
        maxHeight: coords.maxHeight,
        background: 'var(--popover-bg, var(--card-bg))',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
